import { IManufacturerSdResponse } from '@kcftech/prometheus-client';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { NoValue } from 'components/no_value/NoValue';
import { ManufacturersPagedDataSource } from 'custom_hooks/data_hooks/data_source/manufacturersDataSource';
import { ApproveActionButton } from 'pages/Settings/ThirdPartySensorModels/ApprovedSensorBadge';
import { CreateAndApproveManufacturerModal } from 'pages/Settings/ThirdPartySensorModels/ManufacturersPage/ApproveManufacturerModal';
import { useMemo, useState } from 'react';
import { DebugValue } from 'shared/debug/DebugValue';
import { excludeNullAndUndefined } from 'utils/array';
import { getId } from 'utils/data';

export type ActionInfo = Readonly<{
    type: 'approve';
    item: IManufacturerSdResponse;
}>;

export type ManufacturersTableProps = Readonly<{
    dataSource: ManufacturersPagedDataSource;
    isApproved: boolean;
    className?: string;
}>;

export function ManufacturersTable({ dataSource, isApproved }: ManufacturersTableProps) {
    const [action, setAction] = useState<ActionInfo>();

    const { reloadPage } = dataSource;

    const onApproveClose = (updated?: boolean) => {
        if (updated) {
            reloadPage();
        }

        setAction(undefined);
    };

    const columns = useMemo<TableColumn<IManufacturerSdResponse>[]>(
        () =>
            excludeNullAndUndefined([
                {
                    header: () => 'Name',
                    id: 'name',
                    value: ({ rowData }) => (
                        <div className="text-nowrap">
                            {rowData.Name}{' '}
                            <DebugValue description="Manufacturer" value={rowData} />
                        </div>
                    ),
                    primary: true,
                    locked: true,
                    sortOn: 'Name',
                },
                !isApproved
                    ? {
                          header: () => 'Corporation',
                          id: 'corporation',
                          value: ({ rowData }) => (
                              <div className="text-nowrap">
                                  {rowData.Corporation?.CorporationName ?? <NoValue />}
                              </div>
                          ),
                          sortOn: 'Corporation.CorporationName',
                      }
                    : undefined,
                !isApproved
                    ? {
                          header: () => 'Actions',
                          id: 'actions',
                          locked: true,
                          value: ({ rowData }) => (
                              <ActionMenuTableCell>
                                  <ApproveActionButton
                                      onClick={() =>
                                          setAction({ type: 'approve', item: rowData })
                                      }
                                  />
                              </ActionMenuTableCell>
                          ),
                      }
                    : undefined,
            ]),
        [isApproved],
    );

    return (
        <>
            <DataSourceTable
                dataSource={dataSource}
                rowKey={getId}
                columns={columns}
                noResultMessage="No manufacturers found."
            />
            {action?.type === 'approve' && (
                <CreateAndApproveManufacturerModal
                    onClose={onApproveClose}
                    defaultManufacturerName={action.item.Name}
                />
            )}
        </>
    );
}
